/**
 * Social media share URL generators.
 *
 * Platform notes:
 * - Twitter/X: Accepts text + url params. Link preview uses Twitter Card / OG tags.
 * - Facebook: Only accepts url param. Title/description come from OG tags on the page.
 * - LinkedIn: Only accepts url param (shareArticle params are deprecated).
 *   Title/description come from OG tags on the page.
 */

/**
 * Build a Twitter/X share intent URL.
 * @param {string} url - The page URL to share
 * @param {string} [text] - Pre-filled tweet text
 * @returns {string} Twitter intent URL
 */
export function twitterShareUrl(url, text) {
    const params = new URLSearchParams();
    if (text) params.set('text', text);
    params.set('url', url);
    return `https://twitter.com/intent/tweet?${params.toString()}`;
}

/**
 * Build a Facebook sharer URL.
 * @param {string} url - The page URL to share
 * @returns {string} Facebook sharer URL
 */
export function facebookShareUrl(url) {
    const params = new URLSearchParams();
    params.set('u', url);
    return `https://www.facebook.com/sharer/sharer.php?${params.toString()}`;
}

/**
 * Build a LinkedIn share-offsite URL.
 * LinkedIn deprecated shareArticle title/summary params.
 * All preview content comes from Open Graph meta tags on the target page.
 * @param {string} url - The page URL to share
 * @returns {string} LinkedIn share URL
 */
export function linkedInShareUrl(url) {
    const params = new URLSearchParams();
    params.set('url', url);
    return `https://www.linkedin.com/sharing/share-offsite/?${params.toString()}`;
}

/**
 * Build share text for social media platforms.
 * Combines post title with community name when available.
 * @param {string} [title] - Post title
 * @param {string} [communityName] - Community name for context
 * @returns {string} Formatted share text
 */
export function buildShareText(title, communityName) {
    const base = title || 'Check out this post';
    return communityName ? `${base} — ${communityName}` : base;
}

/**
 * Build an absolute permalink URL for a post.
 * Uses REACT_APP_SITE_URL when set, falls back to window.location.origin.
 * @param {string} slug - Community slug
 * @param {string} postId - Post ID
 * @returns {string} Absolute permalink URL, or empty string if inputs missing
 */
export function buildPostPermalink(slug, postId) {
    if (!slug || !postId) return '';
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    return `${siteUrl}/communities/${slug}/posts/${postId}`;
}
